import { getDefaultTaskCode } from 'lib/util';
import moment from 'moment';
import { getWorkingHoursPerWeek } from 'services/allocation.service';
import { getBillRate } from 'services/bill-rate.service';
import { getSwiftWbsCodes } from 'services/project.service';
import { getMonthWiseTimeLineData, getTimeLineData } from 'services/timeline.service';
import { v4 as uuidv4 } from 'uuid';
import { calculateTotalHrsForMonths, getMonths, getTimelinePayload, getWeekdaysCount } from './billable-project/utils';
import AllocationHourRevereseWrapper from './common/allocationHourReverseWrapper';
import { auditFn, auditSA, defaultSpecialProject, demandStatusUILabel, TNT_FUNCTION_CODE } from './constants';
import demandStatusFinder from './demandStatusFinder';
import { setDecimalPrecision } from './util';


const removeDuplicateDelegationResources = (project) => {
    const delegateManager = project?.delegateManager || [];
    const partnerDelegateManager = project?.partnerDelegateManager || [];

    const uniqueNames = new Set();

    const filteredDelegateManager = delegateManager.filter(manager => {
        if (!uniqueNames.has(manager)) {
            uniqueNames.add(manager);
            return true;
        }
        return false;
    });

    const filteredProjectsDelegateManager = partnerDelegateManager.filter(manager => {
        if (!uniqueNames.has(manager)) {
            uniqueNames.add(manager);
            return true;
        }
        return false;
    });

    return { filteredDelegateManager, filteredProjectsDelegateManager }
}

const demandDataMaker = (demandData) => {
    const oppSplitID = demandData?.split?.splitId;
    const splitId = demandData?.split?.id;
    const demandStatusObject = demandStatusFinder(demandData);
    const delegateManager = removeDuplicateDelegationResources(demandData);

    const {
        id,
        role,
        designation,
        noOfHoursPerWeek,
        startDate,
        endDate,
        isCpq,
        functionL1,
        subSector,
        qlId,
        sector,
        serviceArea,
        subServiceArea,
        subSubFunctionDesc,
        industry,
        projects,
        skillGroupClassification,
        skillSet, urgent, projectId, favourites,
        noOfEmployees,
        allocations = [],
        softBlocks = [],
        canViewDetails = false,
        demandCode,
        quotelineId,
        employeeType,
        cec,
        cecRole,
        taskCode,
        taskDescription,
        ratePerHour,
        projectManagerDetails,
        engagementPartnerDetails,
        engagementPartnerInfo,
        projectManagerInfo,
        timeline,
        totalHours,
        clientFee,
        serviceAreaCode,
        subServiceAreaCode,
        subSubFunctionCode,
        functionCode,
        Requisition,
        adjustedUtilizations,
        bulkUpload = false,
        demandStatus,
        autoImportedAt,
        hasDemandShiftTimings,
        hasRequisition,
        gcc
    } = demandData;
    const totalCompleted = allocations.length;
    const timeLineTotalHours = setDecimalPrecision(timeline.reduce((acc, tl) => (((tl.totalHours) || 0) + acc), 0));
    return {
        id,
        bulkUpload,
        isCpq,
        isAutoImported: autoImportedAt,
        demandId: demandCode,
        name: designation,
        skillGroupClassification,
        skillSet,
        deadline: demandStatusObject?.deadline,
        workHours: setDecimalPrecision(noOfHoursPerWeek),
        lightningCount: 2,
        cost: formatIndianNumber(clientFee),
        totalAllocated: noOfEmployees,
        totalCompleted,
        deadLineStatus: demandStatusObject?.deadLineStatus,
        adjustedUtilizations,
        engagementCode: demandData?.sapProject?.engagementCode,
        wbsCode: demandData?.swiftWbsCode?.billable_l4_wbs_code,
        project: {
            projectName: projects?.projectName,
            projectId: projects?.spineCode,
            projectType: projects?.projectType,
            name: projects?.projectType === 'billable' ? projects?.clientOpportunity?.projectName : projects?.projectName,
            projectFunctionCode: projects?.functionCode,
            quoteFunctionCode: projects?.projectType === 'billable' ? projects?.quote?.functionCode : null,
            startDate: projects?.startDate ?? projects?.quote?.startDate,
            endDate: projects?.endDate ?? projects?.quote?.endDate,
            projectServiceArea: projects?.serviceArea ?? projects?.quote?.primaryServiceArea,
            autoImportedAt: projects?.autoImportedAt,
            isSpecialProject: projects?.isSpecialProject,
            gcc: projects?.gcc,
            linkedWbsL4Codes: projects?.linkedWbsL4Codes || null,
            linkedWbsL4Count: projects?.linkedWbsL4Count || 0,
            engagementCode: demandData?.sapProject?.engagementCode,
            opportunityId: projects?.clientOpportunity?.opportunityId,
            id: projects?.id,
            wbsL4Code: (projects?.clientOpportunity?.wbsL4Code)
                || (projects?.linkedWbsL4Count === 1 ? projects?.linkedWbsL4Codes : null),
        },
        Requisition,
        canViewDetails,
        startDate,
        endDate,
        urgent,
        projectId,
        isBookMark: favourites?.length > 0,
        function: functionL1,
        serviceArea,
        subServiceArea,
        subSubFunctionDesc,
        serviceAreaCode,
        subServiceAreaCode,
        subSubFunctionCode,
        functionCode,
        QLID: demandData?.qlId || '-',
        empType: employeeType || '-',
        totalHours: (timeLineTotalHours || totalHours) || '-',
        cec: cec || '-',
        cecRole: cec || '-',
        taskCode,
        taskDescription,
        costPerHour: ratePerHour || '-',
        engManager: projectManagerDetails?.name ? projectManagerDetails.name : (projectManagerInfo?.name ?? '-'),
        engPartner: engagementPartnerDetails?.name ? engagementPartnerDetails.name : (engagementPartnerInfo?.name ?? '-'),
        allocation: allocations?.map((allocation) => ({
            id: allocation?.id,
            profile: allocation?.resource?.profilePicture || '',
            name: allocation?.resource?.name,
            designation: allocation?.resource?.designation,
            subServiceArea: allocation?.resource?.subServiceArea,
            startDate: moment(allocation?.startDate).format('DD/MM/YYYY'),
            endDate: moment(allocation?.endDate).format('DD/MM/YYYY'),
            startDateRaw: allocation?.startDate,
            endDateRaw: allocation?.endDate,
            reportingManager: allocation?.resource?.reportingPartnerDetail?.name,
            hoursPerWeek: AllocationHourRevereseWrapper(allocation.startDate, allocation.endDate, allocation.weeklyHours, allocation.includeSaturday, allocation.includeSunday),
            resourceId: allocation?.resource?.id,
            include: [
                { label: 'Saturday', value: allocation.includeSaturday },
                { label: 'Sunday', value: allocation.includeSunday }
            ],
            grade: allocation?.resource?.grade,
            gradeCode: allocation?.resource?.gradeCode,
            adjustedUtilizations: allocation?.adjustedUtilizations,
            isContinuityResource: allocation?.isContinuityResource,
            continuityReason: allocation?.continuityReason,
            continuityReasonComment: allocation?.continuityReasonComment,
            hasAllocationShiftTimings: allocation?.hasAllocationShiftTimings
        })),
        timeline: getMonthWiseTimeLineData(timeline),
        demandStatus: demandStatusUILabel[demandStatus],
        originalValue: demandData,
        splitId,
        oppSplitID,
        isInactive: demandData?.inactive,
        delegateManager: delegateManager?.filteredDelegateManager || [],
        partnerDelegateManager: delegateManager?.filteredProjectsDelegateManager || [],
        hasDemandShiftTimings,
        hasRequisition: !!hasRequisition,
        isPodWorkpackage: demandData?.podWorkpackage?.id ? true : false,
        podWorkPackage: demandData?.podWorkpackage,
        swiftWbsCode: demandData?.swiftWbsCode || null,
        latestCandidateRequisition: demandData?.latestCandidateRequisition || null
    };
};

export const formatIndianNumber = (number) => {
    if (!number) return '0';
    let strNumber = Math.round(number).toString();
    let lastThree = strNumber.slice(-3);
    let otherNumbers = strNumber.slice(0, -3);
    if (otherNumbers !== '') {
        lastThree = ',' + lastThree;
    }
    let result = otherNumbers.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + lastThree;
    return result;
};

export const parseIndianNumber = (formattedNumber) => {
    if (!formattedNumber) return NaN;

    // Remove commas from the formatted number string
    const numberString = formattedNumber.replace(/,/g, '');

    // Convert the resulting string back to a number
    return parseInt(numberString, 10);
};

export const getProjectDetails = ({ clientOpportunity, quotes }) => ({
    projectName: quotes?.name || clientOpportunity?.projectName,
    oppID: clientOpportunity?.opportunityId,
    quoteID: quotes?.quoteId,
    employmentType: quotes?.employmentType,
    oppSplitID: clientOpportunity?.opportunitySplitId,
    engManager: clientOpportunity?.projectManagerName,
    engPartner: clientOpportunity?.engagementPartnerName,
    startDate: quotes?.startDate ? moment(quotes?.startDate).format('MM-DD-YYYY') : '-',
    endDate: quotes?.endDate ? moment(quotes?.endDate).format('MM-DD-YYYY') : '-',
    client: clientOpportunity?.clientName,
    function: quotes?.primarySubFunction,
    functionCode: quotes?.functionCode,
    serviceArea: quotes?.primaryServiceArea,
    subServiceArea: quotes?.primarySubServiceArea,
    subSubFunction: quotes?.primarySubSubFunction,
    serviceAreaCode: quotes?.serviceAreaCode,
    subServiceAreaCode: quotes?.subFunctionCode,
    subSubFunctionCode: quotes?.subSubFunctionCode,
    rateCard: quotes?.rateCard,
    totalHours: quotes?.budgetedWorkHours || 'N/A',
    clientFee: quotes?.expectedProjectBudget ? formatIndianNumber(quotes?.expectedProjectBudget) : 'N/A',
    opportunityValue: clientOpportunity?.opportunityValue ? formatIndianNumber(clientOpportunity?.opportunityValue) : null,
    opportunityId: clientOpportunity?.opportunityId || '-',
    splitCount: clientOpportunity?.opportunitySplitIdCount,
    wbsL4CodeCount: clientOpportunity?.linkedWbsL4Count,
    wbsL4Codes: clientOpportunity?.linkedWbsL4Codes,
    specialProjects: defaultSpecialProject,
    wbsCodesWithExpandOpportunities: clientOpportunity?.wbsCodesWithExpandOpportunities?.wbsCodes?.split(',')?.slice(0, 2) || [],
    wbsCodesWithExpandOpportunitiesCount: clientOpportunity?.wbsCodesWithExpandOpportunities?.count || 0,
    engagementCodesWithExpandOpportunities: clientOpportunity?.engagementCodesWithExpandOpportunities?.engagementCodes?.split(',')?.slice(0, 2) || [],
    engagementCodesWithExpandOpportunitiesCount: clientOpportunity?.engagementCodesWithExpandOpportunities?.count || 0
});

export const getProjectDetailsFromOpportunity = (clientOpportunity) => ({
    projectName: clientOpportunity?.projectName,
    oppID: clientOpportunity?.opportunityId,
    quoteID: '-',
    oppSplitID: clientOpportunity?.opportunitySplitId || '-',
    engManager: clientOpportunity?.projectManagerName,
    engPartner: clientOpportunity?.engagementPartnerName,
    startDate: clientOpportunity?.oppStartDate ? moment(clientOpportunity?.oppStartDate).format('MM-DD-YYYY') : '-',
    endDate: clientOpportunity?.oppEndDate ? moment(clientOpportunity?.oppEndDate).format('MM-DD-YYYY') : '-',
    client: clientOpportunity?.clientName,

    function: clientOpportunity?.functionClientServiceLevel1,
    functionCode: clientOpportunity?.functionCode,
    serviceArea: clientOpportunity?.serviceArea,
    subServiceArea: clientOpportunity?.subServiceArea,
    serviceAreaCode: clientOpportunity?.serviceAreaCode,
    subServiceAreaCode: clientOpportunity?.subServiceAreaCode,
    subSubFunction: clientOpportunity?.subSubFunctionDesc,
    subSubFunctionCode: clientOpportunity?.subSubFunctionCode,

    rateCard: 'Standard Rate Card',
    totalHours: 'N/A',
    clientFee: clientOpportunity?.opportunityValue ? formatIndianNumber(clientOpportunity?.opportunityValue) : 'N/A',
    opportunityValue: clientOpportunity?.opportunityValue ? formatIndianNumber(clientOpportunity?.opportunityValue) : null,
    specialProjects: defaultSpecialProject,
    opportunityId: clientOpportunity?.opportunityId || '-',
    splitCount: clientOpportunity?.opportunitySplitIdCount,
    wbsL4CodeCount: clientOpportunity?.linkedWbsL4Count,
    wbsL4Codes: clientOpportunity?.linkedWbsL4Codes,
    wbsCodesWithExpandOpportunities: clientOpportunity?.wbsCodesWithExpandOpportunities?.wbsCodes?.split(',')?.slice(0, 2) || [],
    wbsCodesWithExpandOpportunitiesCount: clientOpportunity?.wbsCodesWithExpandOpportunities?.count || 0,
    engagementCodesWithExpandOpportunities: clientOpportunity?.engagementCodesWithExpandOpportunities?.engagementCodes?.split(',')?.slice(0, 2) || [],
    engagementCodesWithExpandOpportunitiesCount: clientOpportunity?.engagementCodesWithExpandOpportunities?.count || 0
});

export const getQuoteLineDetails = ({ quoteLines, startDate, endDate }) => quoteLines?.map((quoteLine) => ({
    quoteLineDetails: quoteLine?.designation,
    startDate: moment(startDate).format('DD-MM-YYYY'),
    endDate: moment(endDate).format('DD-MM-YYYY'),
    function: quoteLine?.employeeFunction?.split(' - ')[0],
    serviceArea: quoteLine?.serviceArea,
    subServiceArea: quoteLine?.subServiceArea,
    subSubFunctionDesc: quoteLine?.subSubFunctionDesc,
    totalHours: quoteLine?.totalHours,
    ratePerHour: quoteLine?.ratePerHour,
    empType: quoteLine?.employeeType,
    skillGroupClassification: quoteLine?.skillGroupClassification,
    talentGroup: quoteLine?.talentGroup,
    clientRate: `₹${quoteLine?.clientFee}/-`,
    cost: (quoteLine?.billRate?.ratePerHour || 0) * quoteLine?.totalHours,
    quotelineId: quoteLine?.demandId
}));

export const getMonthsBetweenDates = (startDate, endDate) => {
    const months = [];
    const currentDate = moment(startDate);

    while (currentDate.isSameOrBefore(endDate, 'month')) {
        const month = currentDate.format('MMM');
        const year = parseInt(currentDate.format('YYYY'));
        months.push({ month, year });

        // Move to the next month
        currentDate.add(1, 'month');
    }

    return months;
};

// const timeLine = getMonthsBetweenDates(startDate, endDate); /** Use for time line calculation */
export const generateTimeLine = ({
    resourceHours, startDate, endDate, totalHours, calculateTimeline
}) => {
    if (!calculateTimeline) {
        const months = getMonths(startDate, endDate);
        const existingTimeLine = resourceHours?.map((resourceHour) => ({
            month: moment(resourceHour?.month, 'MMM').format('MMMM'),
            year: `${resourceHour?.year}`.length === 2 ? moment(resourceHour?.year, 'YY').format('YYYY') : resourceHour?.year,
            hours: resourceHour?.hoursPerMonth || resourceHour?.hours || 0
        }));

        const timeline = months.map((month) => {
            const existingMonth = existingTimeLine.find((m) => (
                month.month === m.month && m.year === month.year
            ));
            if (existingMonth) return existingMonth;
            return {
                ...month,
                hours: 0
            };
        });
        return timeline;
    }
    const months = getMonths(startDate, endDate);
    const totalWorkingDays = getWeekdaysCount(startDate, endDate);
    const hrsPerDay = totalHours / totalWorkingDays;
    const newTimeline = calculateTotalHrsForMonths(months, hrsPerDay, startDate, endDate);
    return newTimeline;
};

export const getHoursPerWeek = ({ totalHours, startDate, endDate }) => {
    const totalWorkingDays = getWeekdaysCount(startDate, endDate);
    const hrsPerDay = totalHours / totalWorkingDays;
    const hrsPerWeek = Math.ceil(hrsPerDay * 5);
    return hrsPerWeek;
};

export const generateDemands = (quoteLine, quote, needApproval = false, isInstantEditEnable = true, calculateTimeline = false, isCpq = false) => {
    return ({
        id: uuidv4(),
        isCpq,
        name: quoteLine?.designation,
        isDisableGrade: quoteLine?.isDisableGrade,
        isArticleGrade: quoteLine?.isArticleGrade,
        QLID: quoteLine?.demandId,
        function: quoteLine?.employeeFunction?.split(' - ')[0],
        serviceArea: quoteLine?.serviceArea,
        subServiceArea: quoteLine?.subServiceArea,
        subSubFunctionDesc: quoteLine?.subSubFunctionDesc,
        functionCode: quoteLine?.functionCode,
        serviceAreaCode: quoteLine?.serviceAreaCode,
        subServiceAreaCode: quoteLine?.subServiceAreaCode,
        subSubFunctionCode: quoteLine?.subSubFunctionCode,
        empType: quoteLine?.employeeType,
        startDate: quote?.startDate,
        endDate: quote?.endDate,
        // skillGroupClassification: quoteLine?.skillGroupClassification,
        skillSet: quoteLine?.skillSet, // not available in mapping
        totalHours: quoteLine?.totalHours,
        rateCard: quoteLine?.rateCardType,
        costPerHour: quoteLine?.ratePerHour,
        CEC: quoteLine?.cec,
        taskCodeAndDescription: quoteLine?.taskDescription,
        taskCode: quoteLine?.taskCode,
        taskDescription: quoteLine?.taskDescription,
        cost: formatIndianNumber(quoteLine?.clientFee),
        location: quoteLine?.projectLocation,
        allocations: quoteLine?.allocations,
        resources: quoteLine?.resources,
        timeline: getTimeLineData({
            demandStartDate: quoteLine?.startDate,
            demandEndDate: quoteLine?.endDate,
            existingTimeLines: isCpq ? quoteLine?.resourceHours : [],
            totalhours: quoteLine?.totalHours,
            hoursPerWeek: quoteLine?.noOfHoursPerWeek
        }),
        resourceHours: quoteLine?.resourceHours,
        requisitions: null,
        isInstantEditEnable: true,
        InstantEditData: isInstantEditEnable,
        cec: quoteLine?.cec || 'NA',
        cecRole: quoteLine?.cecRole || 'Not Applicable',
        cecRoleLabel: quoteLine?.cecRoleLabel || 'Not Applicable',
        isEdited: quoteLine?.isEdited || {},
        needApproval,
        noOfHoursPerWeek: quoteLine?.noOfHoursPerWeek,
        skillGroup: quoteLine?.skillGroupClassification, // quoteLine?.skillGroup,
        comments: quoteLine?.comments,
        skills: quoteLine?.skills,
        secondarySkills: quoteLine?.secondarySkills,
        qualification: quoteLine?.qualification,
        certifications: quoteLine?.certifications,
        industry: quoteLine?.industry,
        sector: quoteLine?.sector,
        subSector: quoteLine?.subSector,
        needsApproval: Boolean(quoteLine?.needsApproval),
        travel: quoteLine?.travel,
        quotelineId: quoteLine?.quotelineId,
        clientFee: quoteLine?.clientFee,
        //
        engManager: quote?.engManager, // name
        engPartner: quote?.engPartner, // name,
        engManagerEmail: quote?.engManagerEmail,
        engPartnerEmail: quote?.engPartnerEmail,
        engManagerId: quote?.engManagerId || quote?.projectManager?.value,
        engPartnerId: quote?.engPartnerId || quote?.engagementPartner?.value,
        projectManager: quoteLine?.projectManager || quote?.projectManager,
        engagementPartner: quoteLine?.projectManager || quote?.engagementPartner,
        isTimelineChanged: quoteLine.isTimelineChanged,
        talentGroup: quoteLine.talentGroup,
        talentGroupName: quoteLine.talentGroupName,
        talentGroupExtCode: quoteLine.talentGroupExtCode,
        splitId: quoteLine?.splitId,
        oppSplitID: quoteLine?.oppSplitID,
        engagementCode: quoteLine?.engagementCode,
        sapProjectId: quoteLine?.sapProjectId,
        sapSplitId: quoteLine?.sapSplitId,
        wbsCode: quoteLine?.wbsCode
    })
};

export const getTotalHrs = (startDate, endDate, hoursPerWeek, ratePerHr) => {
    const totalWorkingDays = getWeekdaysCount(startDate, endDate);
    const hrsPerDay = totalWorkingDays >= 5 ? hoursPerWeek / 5 : hoursPerWeek / totalWorkingDays;
    const totalHours = setDecimalPrecision(hrsPerDay * totalWorkingDays);
    const clientFee = Math.round(totalHours * ratePerHr);
    return { totalHours, clientFee };
};

export const generateDemandFromForm = async (formData, type, sapProjects, opportunitySplits) => {
    const { isCpq } = formData;
    let totalHours;
    let clientFee;
    let rateCardType = formData?.rateCardType?.value;
    const istotalHrsNotChanged = formData?.totalHours === formData?.originalValue?.totalHours;
    const isClientFeeNotChanged = formData?.clientFee === formData?.originalValue?.clientFee;
    const isDateNotChanged = moment(formData?.startDate).isSame(moment(formData.originalValue?.startDate))
        && moment(formData?.endDate).isSame(moment(formData.originalValue?.endDate));
    const isHrsNotChanged = (formData?.hoursPerWeek) == (formData?.originalValue?.noOfHoursPerWeek);
    const isEdit = type === 'Edit';
    const isNotChanged = isEdit && isClientFeeNotChanged && istotalHrsNotChanged && isDateNotChanged && isHrsNotChanged;
    let ratePerHour = formData?.ratePerHour || 0;
    let timelinesData = formData?.resourceHours;
    const { totalHours: hrs } = getTotalHrs(
        formData?.startDate,
        formData?.endDate,
        formData?.hoursPerWeek,
        ratePerHour
    );
    if (isNotChanged) {
        totalHours = formData?.totalHours;
        clientFee = formData.clientFee;
    } else {
        timelinesData = getTimeLineData({
            demandStartDate: formData?.startDate,
            demandEndDate: formData?.endDate,
            existingTimeLines: isCpq ? (formData?.timeline || formData?.resourceHours) : [],
            totalhours: formData?.totalHours,
            hoursPerWeek: formData?.hoursPerWeek
        });

        const timelineHours = setDecimalPrecision(timelinesData.reduce((acc, tl) => (((tl.hours || tl.hoursPerMonth) || 0) + acc), 0));

        if (timelineHours === formData?.originalValue?.totalHours) {
            totalHours = formData?.originalValue?.totalHours;
            clientFee = formData?.originalValue?.clientFee;
            rateCardType = formData?.originalValue?.rateCardType || formData.originalValue.rateCard;
        } else {
            const fetchBillRate = await getBillRate({
                functionCode: formData?.function?.functionCode,
                serviceAreaCode: formData?.serviceArea?.serviceAreaCode,
                subServiceAreaCode: formData?.subServiceArea?.subServiceAreaCode,
                designation: formData?.designation?.value,
                startDate: formData?.startDate,
                skillGroup: formData?.skillGroup?.value,
                cecStatus: formData?.cec?.value,
                employmentType: formData?.employmentType?.value,
                subSubFunctionCode: formData?.subSubFunctionCode
            });
            ratePerHour = fetchBillRate?.entity?.ratePerHour;
            const fee = ratePerHour * hrs;
            totalHours = (isCpq && isEdit) ? timelineHours : hrs;
            clientFee = (isCpq && isEdit) ? timelineHours * ratePerHour : fee;
            rateCardType = 'Standard Cost Rate Card';
        }
    }
    const getSplitIdAndEngCode = () => {
        const engCodeFormData = formData?.wbsCode
        const splitFormData = formData?.oppSplitId
        let splitId = splitFormData?.value
        let oppSplitID = splitFormData?.label
        let wbsCode = engCodeFormData?.label
        let sapProjectId = engCodeFormData?.value
        let sapSplitId = engCodeFormData?.opportunity_split_id
        if (wbsCode && sapSplitId && sapSplitId?.startsWith('OS-')) {
            const splitDetails = opportunitySplits.find(split => split.label === engCodeFormData.opportunity_split_id)
            if (splitDetails) {
                splitId = splitDetails.value
                oppSplitID = splitDetails.label
            }
        }
        return { splitId, oppSplitID, sapProjectId, sapSplitId, wbsCode }
    }
    const defaultSwiftTaskCode = getDefaultTaskCode()
    const quoteLine = {
        designation: formData?.designation?.value,
        isDisableGrade: formData?.designation?.isDisableGrade,
        isArticleGrade: formData?.designation?.grade?.description?.toLocaleLowerCase() === 'article',
        demandId: formData.quoteLineIDReference?.label,
        employeeFunction: formData?.function?.value,
        serviceArea: formData?.serviceArea?.value,
        subServiceArea: formData?.subServiceArea?.value,
        subSubFunctionDesc: formData?.subSubFunction?.value,
        functionCode: formData?.function?.functionCode,
        serviceAreaCode: formData?.serviceArea?.serviceAreaCode,
        subServiceAreaCode: formData?.subServiceArea?.subServiceAreaCode,
        subSubFunctionCode: formData?.subSubFunction?.subSubFunctionCode,
        employeeType: formData?.employmentType?.value,
        startDate: formData?.startDate,
        endDate: formData?.endDate,
        skillGroupClassification: formData?.skillGroupClassification?.value || formData?.skillGroup?.value,
        skillSet: formData?.skillSet?.value, // ?.map((skillset) => skillset.value).join(',') || 'N/A', // not available in mapping
        rateCardType, // removed as per instructions from PM
        ratePerHour, // removed as per instructions from PM
        totalHours,
        clientFee,
        cecRoleLabel: formData?.cecRole?.label,
        cecRole: formData?.cecRole?.value,
        allocations: formData?.allocations,
        resources: formData?.resources,
        cec: formData?.cec?.label,
        projectLocation: formData?.location?.map((loc) => loc.value).join(', '),
        resourceHours: timelinesData, // formData?.resourceHours,
        // fields required by BE
        noOfHoursPerWeek: formData?.hoursPerWeek ? setDecimalPrecision(formData?.hoursPerWeek) : 0,
        skillGroup: formData?.skillGroup?.value,
        comments: formData?.comment,
        skills: formData?.primarySkills?.value,
        secondarySkills: formData?.secondarySkills?.value,
        qualification: formData?.qualifications?.value,
        certifications: formData?.certifications?.value,
        industry: formData?.industry?.value,
        sector: formData?.sector?.value,
        subSector: formData?.subSector?.value,
        needsApproval: true,
        travel: formData?.travel?.value === 'Yes',
        quotelineId: formData.quoteLineIDReference?.value,
        isEdited: formData?.isEdited,
        // need to modify further
        taskCode: (formData?.taskCodeAndDescription?.value) ?? defaultSwiftTaskCode.value,
        taskDescription: (formData?.taskCodeAndDescription?.label) ?? defaultSwiftTaskCode.label,
        isTimelineChanged: isNotChanged ? formData.isTimelineChanged : true,
        talentGroup: formData?.talentGroup?.optionId,
        talentGroupExtCode: formData?.talentGroup?.value,
        talentGroupLabel: formData?.talentGroup?.label,
        talentGroupName: formData?.talentGroup?.label,
        ...getSplitIdAndEngCode()
    };

    const quote = {
        startDate: formData?.startDate,
        endDate: formData?.endDate,
        engManager: formData?.engagementManager?.label, // name
        engPartner: formData?.engagementPartner?.label, // name,
        engManagerEmail: formData?.engagementManager?.email,
        engPartnerEmail: formData?.engagementPartner?.email,
        engManagerId: formData?.engagementManager?.employeeCode,
        engPartnerId: formData?.engagementPartner?.employeeCode,
        projectManager: formData?.projectManager || formData?.engagementManager,
        engagementPartner: formData?.engagementPartner
    };
    return generateDemands(quoteLine, quote, true, false, type !== 'Edit', formData.isCpq);
};

const generateFieldDataForDemand = (demandVal) => {
    if (!demandVal) return null;
    return {
        label: demandVal,
        value: demandVal
    };
};

export const generateFormFromDemand = (demand, type, isWithoutQuote) => {
    if (!demand) return null;
    const formData = {};
    const isCpq = demand?.isCpq;
    const nonOptionFields = ['noOfHoursPerWeek',
        'comments', 'CEC', 'costPerHour',
        'startDate', 'endDate', 'taskCode', 'taskDescription'];
    const optionFields = ['certifications',
        'qualification', 'travel',
        'secondarySkills', 'skills',
        'subSector',
        'sector',
        'industry', 'cecRole', 'cec',
        'rateCard', 'skillGroup',
        'skillGroupClassification', 'location',
        'empType',
        // 'subServiceArea', 'serviceArea', 'function',
        'name',
        // 'QLID',
        'engManager', 'engPartner',
        'talentGroup'
        // 'taskCodeAndDescription'
    ];
    const optionFieldsMapping = {
        // QLID: 'quoteLineIDReference',
        name: 'designation',
        certifications: 'certifications',
        qualification: 'qualifications',
        travel: 'travel',
        secondarySkills: 'secondarySkills',
        skills: 'primarySkills',
        subSector: 'subSector',
        sector: 'sector',
        industry: 'industry',
        cecRole: 'cecRole',
        cec: 'cec',
        rateCard: 'rateCardType',
        // skillSet: 'skillSet',
        skillGroup: 'skillGroup',
        skillGroupClassification: 'skillGroupClassification',
        location: 'location',
        empType: 'employmentType',
        // subServiceArea: 'subServiceArea',
        // serviceArea: 'serviceArea',
        // function: 'function',
        taskCodeAndDescription: 'taskCodeAndDescription',
        talentGroup: 'talentGroup'

    };
    const nonOptionFieldsMapping = {
        noOfHoursPerWeek: 'hoursPerWeek',
        comments: 'comment',
        CEC: 'cec',
        costPerHour: 'ratePerHour',
        startDate: 'startDate',
        endDate: 'endDate',
        taskDescription: 'taskDescription'
    };

    nonOptionFields?.forEach((option) => {
        if (demand[option]) {
            const key = nonOptionFieldsMapping[option];
            formData[key] = demand[option];
        }
    });

    optionFields?.forEach((option) => {
        if (demand[option]) {
            const key = optionFieldsMapping[option];
            formData[key] = { label: demand[option], value: demand[option] };
        }
    });
    const cecResponse = demand.cec;
    // formData.CEC = cecResponse;
    // formData.cec = cecResponse;

    // formData.hoursPerWeek = demand.totalHours / 5;
    if (type === 'Edit' || type === 'Clone') {
        formData.oppSplitId = demand?.oppSplitID && demand?.splitId ? {
            label: demand?.oppSplitID,
            value: demand?.splitId
        } : null;
        formData.engagementCode = demand?.sapProjectId && demand?.engagementCode ? {
            label: demand?.engagementCode,
            splitId: demand?.sapSplitId,
            value: demand?.sapProjectId,
        } : null;

        formData.wbsCode = demand?.sapProjectId && demand?.wbsCode ? {
            label: demand?.wbsCode,
            splitId: demand?.sapSplitId,
            value: demand?.sapProjectId,
        } : null;

        formData.engagementManager = {
            label: demand?.engManager,
            value: demand?.engManagerId,
            email: demand?.engManagerEmail
        };
        formData.engagementPartner = {
            label: demand?.engPartner,
            value: demand?.engPartnerId,
            email: demand?.engPartnerEmail
        };
        formData.cec = {
            label: demand?.cec,
            value: demand?.cec
        };

        formData.cecRole = {
            label: demand?.cecRoleLabel,
            value: demand?.cecRole
        };

        formData.quoteLineIDReference = { label: demand?.QLID, value: demand?.quotelineId };
        formData.engManager = demand?.engManager;
        formData.engPartner = demand?.engPartner;
        formData.engManagerEmail = demand?.engManagerEmail;
        formData.engPartnerEmail = demand?.engPartnerEmail;
        formData.engManagerId = demand?.engManagerId;
        formData.engPartnerId = demand?.engPartnerId;
        formData.cecRole = { label: demand?.cecRoleLabel, value: demand?.cecRole };
        formData.skillSet = demand?.skillSet ? { label: demand?.skillSet, value: demand?.skillSet } : null;
        formData.taskCodeAndDescription = { label: demand?.taskDescription, value: demand?.taskCode };
        formData.function = { label: demand?.function, value: demand?.function, functionCode: demand?.functionCode };
        formData.serviceArea = { label: demand?.serviceArea, value: demand?.serviceArea, serviceAreaCode: demand?.serviceAreaCode };
        formData.subServiceArea = { label: demand?.subServiceArea, value: demand?.subServiceArea, subServiceAreaCode: demand?.subServiceAreaCode },
        formData.subSubFunction = { label: demand?.subSubFunctionDesc, value: demand?.subSubFunctionDesc, subSubFunctionCode: demand?.subSubFunctionCode },
            formData.cecRole = { label: demand?.cecRoleLabel, value: demand?.cecRole };
        formData.skillSet = demand?.skillSet ? { label: demand?.skillSet, value: demand?.skillSet } : null;// ?.split(',')?.map((item) => ({ label: item, value: item })),
        formData.taskCodeAndDescription = (demand?.taskCode && demand?.taskDescription) ? { label: demand?.taskDescription, value: demand?.taskCode } : null;
    }

    if (type === 'Edit') {
        formData.totalHours = demand.totalHours;
        formData.clientFee = parseIndianNumber(demand.cost);
        formData.hoursPerWeek = isCpq
            ? (demand.noOfHoursPerWeek || getWorkingHoursPerWeek({ isTNTFunction: demand?.functionCode === TNT_FUNCTION_CODE, isArticleGrade: Boolean(demand?.isArticleGrade) }))
            : demand.noOfHoursPerWeek;
        formData.isCpq = demand.isCpq;
    } else {
        formData.hoursPerWeek = 0;
    }

    if (demand?.isEdited) {
        formData.isEdited = demand.isEdited || {};
    }
    formData.resourceHours = demand?.resourceHours || [];
    formData.timeline = demand?.timeline || [];
    const travelRes = demand?.travel ? 'Yes' : 'No';
    formData.travel = { label: travelRes, value: travelRes };
    formData.originalValue = demand;
    formData.allocations = demand.allocations;
    formData.resources = demand.resources;
    if (isWithoutQuote) {
        formData.quoteLineIDReference = { label: 'NA', value: null }
    }
    return formData;
};

const convertEngagementManagerAndPartner = (details, resourceCode) => {
    return {
        label: details?.name,
        value: resourceCode,
        email: details?.email
    }
}

export const generateDemandsDefaultValue = (demand, sapProjects = []) => {
    const [allocation] = demand?.allocations ?? [];
    const formData = {
        designation: generateFieldDataForDemand(demand?.designation),
        quoteLineIDReference: generateFieldDataForDemand(demand?.qlId),
        function: { ...generateFieldDataForDemand(demand?.functionL1), functionCode: demand?.functionCode },
        serviceArea: { ...generateFieldDataForDemand(demand?.serviceArea), serviceAreaCode: demand?.serviceAreaCode },
        subServiceArea: { ...generateFieldDataForDemand(demand?.subServiceArea), subServiceAreaCode: demand?.subServiceAreaCode },
        functionCode: demand?.functionCode,
        subServiceAreaCode: demand?.subServiceAreaCode,
        serviceAreaCode: demand?.serviceAreaCode,
        employmentType: generateFieldDataForDemand(demand?.employeeType),
        location: demand?.projectLocation ? generateFieldDataForDemand(demand?.projectLocation) : generateFieldDataForDemand(demand?.officeLocation),
        startDate: new Date(demand?.startDate).toUTCString(),
        endDate: new Date(demand?.endDate).toUTCString(),
        skillGroupClassification: generateFieldDataForDemand(demand?.skillGroupClassification),
        skillGroup: generateFieldDataForDemand(demand?.skillGroup),
        skillSet: generateFieldDataForDemand(demand?.skillSet),
        rateCardType: demand?.rateCardType ? { label: `${demand?.rateCardType} hours`, value: demand?.rateCardType } : null,
        ratePerHour: Number(demand?.ratePerHour),
        cec: generateFieldDataForDemand(demand?.cec),
        cecRole: generateFieldDataForDemand(demand?.cecRole),
        talentGroup: generateFieldDataForDemand(demand?.talentGroupExtCode),
        taskCodeAndDescription: { label: demand?.taskDescription, value: demand?.taskCode }, // generateFieldDataForDemand(demand?.taskCode)
        industry: generateFieldDataForDemand(demand?.industry),
        sector: generateFieldDataForDemand(demand?.sector),
        subSector: generateFieldDataForDemand(demand?.subSector),
        primarySkills: generateFieldDataForDemand(demand?.skills),
        secondarySkills: generateFieldDataForDemand(demand?.secondarySkills),
        travel: { label: demand?.travel ? 'Yes' : 'No', value: demand?.travel ? 'Yes' : 'No' },
        qualifications: generateFieldDataForDemand(demand?.qualification),
        certifications: generateFieldDataForDemand(demand?.certification),
        comment: demand?.comments,
        adjustedUtilizations: demand?.adjustedUtilizations,
        engagementManager: demand?.projectManagerDetails?.name ? convertEngagementManagerAndPartner(demand?.projectManagerDetails) : convertEngagementManagerAndPartner(demand?.projectManagerInfo),
        engagementPartner: demand?.engagementPartnerInfo?.name ? convertEngagementManagerAndPartner(demand?.engagementPartnerInfo) : convertEngagementManagerAndPartner(demand?.engagementPartnerDetails),
        hoursPerWeek: demand?.noOfHoursPerWeek,
        resources: allocation ? [{
            ...allocation,
            resourceName: { ...allocation?.resource, isContinuityResource: allocation?.isContinuityResource },
            gradeCode: allocation?.resource?.gradeCode,
            name: allocation?.resource?.name,
            designation: allocation?.resource?.designation,
            serviceArea: allocation?.resource?.serviceArea,
            hoursPerWeek: AllocationHourRevereseWrapper(allocation.startDate, allocation.endDate, allocation.weeklyHours, allocation.includeSaturday, allocation.includeSunday)
        }] : [],
        oppSplitId: demand?.split ? {
            label: demand?.split?.splitId,
            value: demand?.split?.id
        } : null,
        engagementCode: demand?.sapProject ? {
            label: demand?.sapProject?.engagementCode,
            value: demand?.sapProject?.id,
            splitId: demand?.sapProject?.splitId
        }
            : null,
        wbsCode: demand?.swiftWbsCode ? {
            label: demand?.swiftWbsCode?.billable_l4_wbs_code, value: demand?.swiftWbsCode?.id, opportunity_split_id: demand?.swiftWbsCode?.opportunity_split_id
        } : null,
        hasRequisition: !!demand?.hasRequisition,
        isCpq: demand?.isCpq,
    };
    return formData;
};

export const calculateAccumulatedBudget = (demands) => {
    let accumulatedBudget = 0;
    demands?.map((demand) => {
        accumulatedBudget += demand.clientFee;
        return 0;
    });
    return accumulatedBudget;
};

export const generateDemandsFromQuoteLine = async (quote, clientOpportunity, fnMasterData, talentGroupMasterData) => {
    const P2WBudget = quote?.expectedProjectBudget;
    let demandSwiftDetails = {}
    const opportunitySplits = clientOpportunity?.opportunitySplits?.map((split) => ({ label: split?.splitId, value: split?.id })) ?? []
    if (opportunitySplits?.length === 1) {
        demandSwiftDetails.splitId = opportunitySplits[0].value
        demandSwiftDetails.oppSplitID = opportunitySplits[0].label
    }
    if (clientOpportunity?.linkedWbsL4Count === 1) {
        const response = await getSwiftWbsCodes({
            offset: 0, limit: 1, search: '', opportunityId: clientOpportunity?.opportunityId
        })
        if (response?.status && response?.entity?.count === 1) {
            const engCodeFormData = response.entity.rows[0]
            demandSwiftDetails.wbsCode = engCodeFormData?.billable_l4_wbs_code
            demandSwiftDetails.sapProjectId = engCodeFormData?.id
            demandSwiftDetails.sapSplitId = engCodeFormData?.opportunity_split_id

            demandSwiftDetails.engPartner = engCodeFormData?.wbsEngagementPartner?.name
            demandSwiftDetails.engPartnerEmail = engCodeFormData?.wbsEngagementPartner?.email
            demandSwiftDetails.engPartnerId = engCodeFormData?.wbsEngagementPartner?.employeeCode
            demandSwiftDetails.engManager = engCodeFormData?.wbsProjectManager?.name
            demandSwiftDetails.engManagerEmail = engCodeFormData?.wbsProjectManager?.email
            demandSwiftDetails.engManagerId = engCodeFormData?.wbsProjectManager?.employeeCode

            if (demandSwiftDetails?.wbsCode && demandSwiftDetails?.sapSplitId && demandSwiftDetails?.sapSplitId?.startsWith('OS-')) {
                const splitDetails = opportunitySplits.find(split => split.label === engCodeFormData.opportunity_split_id)
                if (splitDetails) {
                    demandSwiftDetails.splitId = splitDetails.value
                    demandSwiftDetails.oppSplitID = splitDetails.label
                } else {
                    demandSwiftDetails.splitId = null
                    demandSwiftDetails.oppSplitID = null
                }
            }
        }
    }
    demandSwiftDetails = Object.fromEntries(
        Object.entries(demandSwiftDetails).filter(([_, value]) => value !== null && value !== undefined && value !== '')
    );
    const demands = quote?.quoteLines?.map((quoteLine) => {
        const fn = fnMasterData?.find((fn) => fn?.name?.toLowerCase() === quoteLine?.employeeFunction?.split(' - ')[0]?.toLocaleLowerCase());
        const serviceArea = fn?.serviceAreas?.find((sa) => sa?.name?.toLocaleLowerCase() === quoteLine?.serviceArea?.toLocaleLowerCase());
        const subServiceArea = serviceArea?.subServiceAreas?.find(
            (ssa) => ssa?.name?.toLocaleLowerCase() === quoteLine?.subServiceArea?.toLocaleLowerCase()
        );

        const talentGroup = talentGroupMasterData?.find((tg) => tg?.serviceAreaCode === quoteLine?.serviceAreaCode
            && tg?.subServiceAreaCode === quoteLine?.subServiceAreaCode
            && tg?.functionCode === quoteLine?.functionCode
            && tg?.name === quoteLine?.talentGroup);

        return generateDemands({
            ...quoteLine,
            cec: quoteLine?.cec === 'Non CEC' ? 'NA' : quoteLine?.cec,
            functionCode: quoteLine?.functionCode || fn?.functionCode,
            subServiceAreaCode: quoteLine?.subServiceAreaCode || subServiceArea?.subServiceAreaCode,
            serviceAreaCode: quoteLine?.serviceAreaCode || serviceArea?.serviceAreaCode,
            subSubFunctionCode: quoteLine?.subSubFunctionCode,
            projectLocation: clientOpportunity?.engagementPartnerInfo?.officeLocation,
            talentGroup: talentGroup?.optionId,
            talentGroupName: talentGroup?.name,
            talentGroupExtCode: talentGroup?.externalCode,
            talentGroupLabel: talentGroup?.name,
            ...demandSwiftDetails
        }, {
            ...quote,
            engManager: clientOpportunity?.projectManagerName,
            engPartner: clientOpportunity?.engagementPartnerName,
            engManagerEmail: clientOpportunity?.projectManagerEmail,
            engPartnerEmail: clientOpportunity?.engagementPartnerEmail,
            engManagerId: clientOpportunity?.projectManager,
            engPartnerId: clientOpportunity?.engagementPartnerName,
            ...demandSwiftDetails
        }, false, true, false, true);
    });

    const AccumulatedBudget = calculateAccumulatedBudget(quote?.quoteLines);
    return { demands, P2WBudget, AccumulatedBudget };
};

export const commonMappingFn = (item) => ({
    ...item,
    label: item?.name,
    value: item?.name
});

export const getUniqueQuotelineAlignments = (demands) => {
    const functions = new Set();
    const serviceArea = new Set();
    const subServiceArea = new Set();
    demands?.forEach((d) => {
        functions.add(d?.functionCode);
        serviceArea.add(d?.serviceAreaCode);
        subServiceArea.add(d?.subServiceAreaCode);
    });
    return {
        functions: Array.from(functions),
        serviceArea: Array.from(serviceArea),
        subServiceArea: Array.from(subServiceArea)
    }
};

export const getAlignmentFilterData = ({ demands, masterData }) => {
    const { functions, serviceArea, subServiceArea } = getUniqueQuotelineAlignments(demands);
    const functionsOptions = masterData?.functions
        ?.filter((fn) => functions.includes(fn.functionCode))
        ?.map((item) => ({
            ...item,
            label: item?.name,
            value: item?.functionCode
        }));
    const serviceAreaOptions = functionsOptions
        ?.flatMap((option) => (option.serviceAreas?.map((v) => ({ ...v, functionCode: option.functionCode }))))
        ?.filter((sa) => serviceArea.includes(sa.serviceAreaCode))
        ?.map((item) => ({
            ...item,
            label: item?.name,
            value: item?.serviceAreaCode
        }));
    const subServiceAreaOptions = serviceAreaOptions
        ?.flatMap((option) => option.subServiceAreas?.map((v) => ({ ...v, serviceAreaCode: option.serviceAreaCode })))
        ?.filter((ssa) => subServiceArea.includes(ssa.subServiceAreaCode))
        ?.map((item) => ({
            ...item,
            label: item?.name,
            value: item?.subServiceAreaCode
        }));
    return { functionsOptions, serviceAreaOptions, subServiceAreaOptions };
};

export const getAlignmentFilterDataFromDemandFilters = ({ demandFilters = {}, masterData }) => {
    const { functionCodes: functions, serviceAreaCodes: serviceArea, subServiceAreaCodes: subServiceArea, designations } = demandFilters ?? {};
    const functionsOptions = masterData?.functions
        ?.filter((fn) => functions?.includes(fn.functionCode))
        ?.map((item) => ({
            ...item,
            label: item?.name,
            value: item?.functionCode
        }));
    const serviceAreaOptions = functionsOptions
        ?.flatMap((option) => (option.serviceAreas?.map((v) => ({ ...v, functionCode: option.functionCode }))))
        ?.filter((sa) => serviceArea?.includes(sa.serviceAreaCode))
        ?.map((item) => ({
            ...item,
            label: item?.name,
            value: item?.serviceAreaCode
        }));
    const subServiceAreaOptions = serviceAreaOptions
        ?.flatMap((option) => option.subServiceAreas?.map((v) => ({ ...v, serviceAreaCode: option.serviceAreaCode })))
        ?.filter((ssa) => subServiceArea?.includes(ssa.subServiceAreaCode))
        ?.map((item) => ({
            ...item,
            label: item?.name,
            value: item?.subServiceAreaCode
        }));
    const designationOptions = designations.map(designation => ({ label: designation, value: designation }))
    return { functionsOptions, serviceAreaOptions, subServiceAreaOptions, designations: designationOptions };
};

export const isMandatoryFieldEmpty = (demands) => {
    const condition = demands.map((demand) => {
        const isStartDateNotEmpty = Boolean(demand?.startDate);
        const isEndDateNotEmpty = Boolean(demand?.endDate);
        const isSkillGroupNotEmpty = Boolean(demand?.skillGroup);
        const isSkillSetNotEmpty = Boolean(demand?.skillGroup);
        const isEngManagerNotEmpty = Boolean(demand?.engManager);
        const isEngPartnerNotEmpty = Boolean(demand?.engPartner);
        const isLocationNotEmpty = Boolean(demand?.location);
        const isTaskCodeAndDescriptionNotEmpty = demand?.function?.toLocaleLowerCase() !== 'audit'
            ? true
            : Boolean(demand?.taskCodeAndDescription);
        return isStartDateNotEmpty
            && isEndDateNotEmpty
            && isSkillGroupNotEmpty
            && isSkillSetNotEmpty
            && isEngManagerNotEmpty
            && isEngPartnerNotEmpty
            && isLocationNotEmpty
            && isTaskCodeAndDescriptionNotEmpty;
    });
    return condition.includes(false);
};

export const getInvalidDemands = (demands) => {
    console.log("🚀 ~ getInvalidDemands ~ demands:", demands.length)
    const items = demands.filter((demand) => {
        const isStartDateNotEmpty = Boolean(demand?.startDate);
        const isEndDateNotEmpty = Boolean(demand?.endDate);
        const isSkillGroupNotEmpty = Boolean(demand?.skillGroup);
        const isSkillSetNotEmpty = Boolean(demand?.skillSet);
        const isEngManagerNotEmpty = Boolean(demand?.engManager);
        const isEngPartnerNotEmpty = Boolean(demand?.engPartner);
        const isLocationNotEmpty = Boolean(demand?.location);
        const isCec = Boolean(demand?.cec);
        const isCecRole = Boolean(demand?.cecRole);
        const isTaskCodeAndDescriptionNotEmpty = demand?.function?.toLocaleLowerCase() === auditFn.name?.toLocaleLowerCase() && demand?.serviceArea?.toLocaleLowerCase() === auditSA.label?.toLocaleLowerCase()
            ? Boolean(demand?.taskCode)
            : true;

        return !isStartDateNotEmpty
            || !isEndDateNotEmpty
            || !isSkillGroupNotEmpty
            || !isSkillSetNotEmpty
            || !isEngManagerNotEmpty
            || !isEngPartnerNotEmpty
            || !isLocationNotEmpty
            || !isTaskCodeAndDescriptionNotEmpty
            || !isCec
            || !isCecRole;
    });
    return items;
};
export const createDemandPayload = (userId, sendApproval, demands) => demands?.map((demand) => (
    {
        //  budget related data:
        budgetExceeded: demand.budgetExceeded,
        allocationCost: demand?.allocations?.allocationCost || demand?.allocations?.[0]?.allocationCost || 0,
        userId,
        designation: demand?.name,
        projectLocation: demand?.location || 'N/A',
        startDate: new Date(demand?.startDate).toUTCString(),
        endDate: new Date(demand?.endDate).toUTCString(),
        skillGroupClassification: demand?.skillGroupClassification,
        functionL1: demand?.function,
        serviceArea: demand?.serviceArea,
        subServiceArea: demand?.subServiceArea,
        subSubFunctionDesc: demand?.subSubFunctionDesc,
        subSubFunctionCode: demand?.subSubFunctionCode,
        functionCode: demand?.functionCode,
        serviceAreaCode: demand?.serviceAreaCode,
        subServiceAreaCode: demand?.subServiceAreaCode,
        ratePerHour: demand?.costPerHour,
        cec: demand?.CEC,
        officeLocation: demand?.location || 'N/A',
        employeeType: demand?.empType,
        role: 'N/A',
        noOfEmployees: 1,
        noOfHoursPerWeek: setDecimalPrecision(demand.noOfHoursPerWeek) || getWorkingHoursPerWeek({ isTNTFunction: demand?.functionCode === TNT_FUNCTION_CODE, isArticleGrade: Boolean(demand?.isArticleGrade) }),
        hoursPerMonth: 0,
        hiringType: 'Internal Sourcing',
        travel: demand?.travel ? demand?.travel === 'Yes' : true,
        skillGroup: demand?.skillGroup || 'N/A', // need to be manually selected by user like skillset (to be addressed)
        skillSet: demand?.skillSet || 'N/A', // manually selected
        cecRole: demand?.cecRoleLabel || 'N/A', // need to be manually selected by user like skillset (to be addressed)
        secondment: false,
        // optional demand fields,
        timeline: demand?.timeline,
        comments: demand?.comments,
        skills: demand?.skills,
        qualification: demand?.qualification,
        certifications: demand?.certifications,
        industry: demand?.industry,
        sector: demand?.sector,
        subSector: demand?.subSector,
        secondmentOffice: demand?.secondmentOffice,
        //
        status: 'approved',
        quotelineId: demand?.quotelineId,
        qlId: demand?.QLID,
        rateCardType: demand?.rateCard,
        projectManager: demand?.projectManagerEmail,
        engagementPartner: demand?.engagementPartnerEmail,
        taskDescription: demand?.taskDescription,
        totalHours: Math.ceil(demand?.totalHours),
        clientFee: parseInt(demand?.clientFee),
        changes: demand?.isEdited,
        resources: (demand.resources || []),
        taskCode: demand?.taskCode,
        projectManagerEmail: demand?.engManagerEmail,
        engagementPartnerEmail: demand?.engPartnerEmail,
        isCpq: demand?.isCpq,
        talentGroup: demand?.talentGroup,
        talentGroupExtCode: demand?.talentGroupExtCode,
        talentGroupLabel: demand?.talentGroupName,
        isDisableGrade: demand?.isDisableGrade,
        splitId: demand?.splitId,
        swiftWbsId: demand?.sapProjectId
    }
));

export const addMoreDetailsToDemand = (updatedFields, demand) => {
    const forkedDemandDetails = { ...demand };
    Object.keys(updatedFields)?.map((key) => {
        forkedDemandDetails[key] = updatedFields[key] && (typeof (updatedFields[key]) === 'object') ? updatedFields[key].value : updatedFields[key];
        return null;
    });

    return forkedDemandDetails;
};

export const getConsumedBudget = (demands, budget) => demands?.reduce((acc, demand) => {
    const totalCost = acc + ((demand?.allocations && demand?.allocations[0]?.allocationCost) || 0);
    demand.budgetExceeded = Boolean((totalCost > budget) && demand?.allocations?.allocationCost);
    return totalCost;
}, 0);
export default demandDataMaker;
